// src/components/auth/AuthField.jsx
// Polished labelled input shared by the auth forms.
// - password variant gets a show/hide eye toggle + Caps Lock warning
// - inline error/help text surfaced under the field (no silent failures)
import React, { useState } from "react";
import { HiEye, HiEyeOff, HiExclamationCircle } from "react-icons/hi";

export default function AuthField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  required = false,
  error,            // string — inline error shown under the field
  accentRing = "focus:ring-indigo-300", // tailwind ring color for focus
  icon: Icon,       // optional leading icon (react-icons component)
}) {
  const isPassword = type === "password";
  const [show, setShow] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  const inputType = isPassword ? (show ? "text" : "password") : type;

  const handleKey = (e) => {
    if (!isPassword) return;
    // getModifierState is supported on all evergreen browsers — no deps needed.
    if (typeof e.getModifierState === "function") {
      setCapsOn(e.getModifierState("CapsLock"));
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>

      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none" />
        )}
        <input
          id={id}
          name={id}
          type={inputType}
          value={value}
          onChange={onChange}
          onKeyUp={handleKey}
          onBlur={() => setCapsOn(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={!!error}
          className={`w-full border rounded-xl py-3 text-sm transition-colors focus:outline-none focus:ring-2 ${accentRing} ${
            Icon ? "pl-10" : "pl-4"
          } ${isPassword ? "pr-11" : "pr-4"} ${
            error ? "border-red-400 bg-red-50" : "border-gray-200"
          }`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {show ? <HiEyeOff className="text-lg" /> : <HiEye className="text-lg" />}
          </button>
        )}
      </div>

      {capsOn && !error && (
        <p className="flex items-center gap-1 text-[11px] text-amber-600 mt-1">
          <HiExclamationCircle className="text-sm" /> Caps Lock is on
        </p>
      )}
      {error && (
        <p className="flex items-center gap-1 text-[11px] text-red-500 mt-1">
          <HiExclamationCircle className="text-sm" /> {error}
        </p>
      )}
    </div>
  );
}
